import { z } from 'zod'

/**
 * PR8.5 integration spike — client half of the critical-confirmation handshake.
 *
 * The engine gate (PR4b-wire, ADR-VOICE-003) mints a single-use token after a
 * clean precondition dry-run and serializes it via `toolError(refusal)`. The wire
 * shape is the JSON that `axiomgo-engine/src/mcp/types.ts#toolError` emits for a
 * `confirmation_required` StructuredToolError:
 *
 *   { error: 'confirmation_required', message, retryable?, confirmationToken,
 *     expiresAt (ISO), speakable? }
 *
 * SPIKE FINDING (Gap B): there is currently NO structured transport carrying this
 * payload from engine → client. The gate emits it inside a tool-error JSON that
 * the orchestrator feeds back to the LLM; the SSE `onResponse` has no confirmation
 * channel. This module is the ready client half — it parses the shape whether it
 * eventually arrives as `richContent.voiceConfirmation` (recommended fix) or as a
 * raw tool-error `content[].text`.
 */

/** Wire shape of the engine's `confirmation_required` emission. */
export const voiceConfirmationRequiredSchema = z.object({
  error: z.literal('confirmation_required'),
  message: z.string(),
  retryable: z.boolean().optional(),
  /** Single-use token; replayed verbatim in the next complete_work_order args. */
  confirmationToken: z.string().min(1),
  /** ISO-8601 expiry (engine TTL 90s). */
  expiresAt: z.string(),
  /** pt-BR prompt the client may speak ("Confirme para concluir… N segundos"). */
  speakable: z.string().optional(),
  /**
   * CORR-B F3 — when true the confirmation requires a closing note from the
   * technician before the action can proceed. The chat renders a textarea with
   * character counter and sends the note back as `closeNote` in the options bag.
   */
  noteRequired: z.boolean().optional(),
  /**
   * Minimum character count for the note (set by the engine; comes from F1
   * histogram). When absent the UI shows the counter but does not enforce a
   * floor — the engine is the authority on rejection.
   */
  minChars: z.number().int().positive().optional(),
})
export type VoiceConfirmationRequired = z.infer<typeof voiceConfirmationRequiredSchema>

/**
 * Extract a `confirmation_required` handshake from a candidate (a JSON string —
 * tool-error text or a structured field — or an already-parsed object). Returns
 * the typed handshake, or `null` when the candidate is not a well-formed
 * confirmation emission. PURE; never throws.
 *
 * Returning null on a missing/empty `confirmationToken` is deliberate: without the
 * exact token the client CANNOT complete the single-use replay, so a tokenless
 * "confirmation_required" is treated as no handshake (fail-closed — the client
 * never claims it can confirm when it cannot).
 */
export function parseVoiceConfirmation(candidate: unknown): VoiceConfirmationRequired | null {
  let obj: unknown = candidate
  if (typeof candidate === 'string') {
    try {
      obj = JSON.parse(candidate)
    } catch {
      return null
    }
  }
  const parsed = voiceConfirmationRequiredSchema.safeParse(obj)
  return parsed.success ? parsed.data : null
}

/**
 * Client-side expiry guard before replaying the token. An unparseable date is
 * treated as expired (fail-closed) so a malformed `expiresAt` can never authorize
 * a stale destructive action.
 */
export function isConfirmationExpired(
  c: VoiceConfirmationRequired,
  now: number = Date.now(),
): boolean {
  const t = Date.parse(c.expiresAt)
  return Number.isNaN(t) || t <= now
}
